import { useQuery, useLazyQuery } from '@apollo/react-hooks';
import DefaultErrorPage from 'next/error';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Box } from '../../components/Flex';
import GraphQLErrorText from '../../components/GraphQLErrorText';
import { BotSummaryBody, HeadingOne } from '../../components/Text';
import { LOCAL_STORAGE } from '../../constants';
import { addApolloState, initializeApollo } from '../../lib/apollo';
import { getBotSummaryBySlug } from '../../lib/blog';
import {
  ALL_BOTS_LIST,
  BOT,
  BOT_LISTINGS_BARE,
  ME_STRIPE_SELLER,
} from '../../lib/graphql/queries';
import markdownToHtml from '../../lib/markdownToHtml';
import {
  TIDAL_URL,
  WHITE_LABEL_ACCENT_COLOR,
  WHITE_LABEL_BRAND,
  WHITE_LABEL_MARKET,
  WHITE_LABEL_TWITTER,
  WHITE_LABEL_URL,
} from '../../lib/white-label';
import RentalHistory from '../../components/bots/rental-history';
import { DesktopComputerIcon, GlobeIcon } from '@heroicons/react/solid';
import { useScrollBlock } from '../../utils/UseScrollBlock';
import { NextSeo } from 'next-seo';
import { analytics } from '../../lib/analytics';
import { BadgeCheck } from 'lucide-react';
import { Tooltip } from 'react-tooltip';
import NextError from 'next/error';
import { trackEvent } from 'next-intercom';
import Button from '../../components/bots/common/Button';
import dynamic from 'next/dynamic';
import { RentalSideCard } from '../../components/bots/RentalSideContainer';

const Tour = dynamic(() => import('reactour'), { ssr: false });
const RentalSideContainer = dynamic(
  () => import('../../components/bots/RentalSideContainer'),
  {
    ssr: false,
    loading: ({}) => (
      <>
        <RentalSideCard>
          <div className="flex-1 flex-col">
            <h3
              className="h-14 bg-gray-200 rounded-md dark:bg-gray-700"
              style={{ width: '40%' }}
            ></h3>

            <ul className="mt-10 space-y-8">
              <li className="w-full h-12 bg-gray-200 rounded-md dark:bg-gray-700"></li>
              <li className="w-full h-12 bg-gray-200 rounded-md dark:bg-gray-700"></li>
              <li className="w-full h-12 bg-gray-200 rounded-md dark:bg-gray-700"></li>
            </ul>
          </div>
        </RentalSideCard>
      </>
    ),
  },
);

const Container = styled.div`
  width: 100%;
  margin-right: auto;
  margin-left: auto;
`;

type Props = {
  token: string;
  affiliateId: string | null;
  summary?: {
    content: string;
    coverImage: string;
    title: string;
  };
  username?: string;
};

function Expanded(props: Props) {
  const router = useRouter();
  const botId = router.query.id;
  const [description, setDescription] = useState('');
  const {
    data,
    error,
    refetch: refetchBot,
  } = useQuery(BOT, {
    variables: {
      id: botId,
    },
  });

  const {
    data: botListingQueryData,
    loading: botListingLoading,
    refetch,
  } = useQuery(BOT_LISTINGS_BARE, {
    variables: {
      botId: botId,
    },
    fetchPolicy: 'no-cache',
    ssr: false,
  });

  const dataLoading = botListingLoading;

  let botListingData;
  let myOwnListingIds;

  if (botListingQueryData) {
    botListingData = botListingQueryData.botListingCounts;
    myOwnListingIds = botListingQueryData.myOwnListingIds;
  }

  const [getMe, { loading: getMeLoading }] = useLazyQuery(ME_STRIPE_SELLER);

  const onCreateListing = async () => {
    const { data, error } = await getMe();

    if (!error && data?.me && data?.me?.isStripeSellerAccountApproved) {
      localStorage.setItem(LOCAL_STORAGE.LISTING_BOT_ID, String(botId));
      router.push(`/bots/new-create-listing?botId=${botId}`);
      return;
    }
    if (
      confirm(
        'Your Stripe seller account must be approved to create a listing. Would you like to visit your settings?',
      )
    ) {
      router.push('/dashboard/settings');
    }
  };

  if (
    error &&
    error.graphQLErrors &&
    error.graphQLErrors[0] &&
    error.graphQLErrors[0].message === 'Not Found'
  ) {
    return <DefaultErrorPage statusCode={404} />;
  }

  if (!data?.bot || (data && data.bot.status !== 'ACTIVE')) {
    return <DefaultErrorPage statusCode={404} />;
  }
  if (error) return <GraphQLErrorText error={error} />;

  const metaTitle = `${data.bot.name} Rentals | Rent ${data.bot.name} on ${WHITE_LABEL_MARKET}`;

  let metaDescription = 'Placeholder description';

  if (data.bot.description) {
    metaDescription = data.bot.description.replace(
      '%_WHITE_LABEL_MARKET_%',
      WHITE_LABEL_MARKET,
    );
  }

  useEffect(() => {
    setDescription(metaDescription);
  }, []);

  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [numberOfListings, setNumberOfListings] = useState({
    user: 0,
    all: 0,
  });

  const [tourSteps, setTourSteps] = useState([]);
  const [tourStep, setTourStep] = useState(0);
  const [isTourOpen, setTourOpen] = useState(false);
  const [blockScroll, allowScroll] = useScrollBlock();

  const closeTour = () => {
    localStorage.setItem(
      isConfirmModalVisible
        ? LOCAL_STORAGE.HAS_TOURED_LISTING_PURCHASE_CONFIRM
        : LOCAL_STORAGE.HAS_TOURED_LISTING_PURCHASE,
      'true',
    );
    setTourOpen(false);
    setTourStep(0);
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    allowScroll();
  };

  const getTourSteps = () => {
    let steps;

    if (isConfirmModalVisible) {
      steps = [
        {
          selector: '[data-tour="step-8"]',
          content: (
            <p>
              Through this window you can review your rental's information for
              the last time before completing the purchase
            </p>
          ),
        },
        {
          selector: '[data-tour="step-9"]',
          content: (
            <p>
              Here is displayed the bot you're about to purchase and its general
              information
            </p>
          ),
        },
        {
          selector: '[data-tour="step-10"]',
          content: (
            <p>
              Here is displayed the exact start date and time of this rental
            </p>
          ),
        },
        {
          selector: '[data-tour="step-11"]',
          content: (
            <p>Here is displayed the exact end date and time of this rental</p>
          ),
        },
        {
          selector: '[data-tour="step-12"]',
          content: (
            <p>This section goes over the price(s) and fee(s) of this rental</p>
          ),
        },
        {
          selector: '[data-tour="step-13"]',
          content: (
            <p>
              When you have read the Terms and Conditions of this rental, you
              can click this checkbox and proceed
            </p>
          ),
        },
        {
          selector: '[data-tour="step-14"]',
          content: (
            <p>
              If everything checks-out and you have aknowledged and accepted the
              Terms and Conditions, you can click this button to be redirected
              to the auto-generated checkout
            </p>
          ),
        },
      ];
    } else {
      steps = [
        {
          selector: '[data-tour="step-1"]',
          content: (
            <p>
              Here is displayed the name of the bot currently picked to rent
            </p>
          ),
        },
        {
          selector: '[data-tour="step-3"]',
          content: (
            <p>
              In this section by clicking, you access a calendar in which you
              can pick your rental's start date
            </p>
          ),
        },
        {
          selector: '[data-tour="step-5"]',
          content: (
            <p>
              Using these quick select options, you can easily pick an end date
              for your rental.
            </p>
          ),
        },
        {
          selector: '[data-tour="step-4"]',
          content: (
            <p>
              In this section by clicking, you may access a calendar in which
              you can pick your custom end date.
            </p>
          ),
        },
        {
          selector: '[data-tour="step-6"]',
          content: (
            <p>
              This section displays your rental's extra information such as the
              exact time when your rental starts and ends in your local time
              zone
            </p>
          ),
        },
        {
          selector: '[data-tour="step-2"]',
          content: (
            <p>
              This displays the activity of the bot, such as the last time this
              bot was rented out and for how much
            </p>
          ),
        },
        {
          selector: '[data-tour="step-7"]',
          content: (
            <p>
              When you're satifised with your choices, you can press this button
              to begin the checkout
            </p>
          ),
        },
      ];
    }

    if (document) {
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];

        const element = document.querySelector(step.selector);
        if (element && element.offsetParent === null) {
          steps = steps.filter((el) => el.selector != step.selector);
        } else {
          // @ts-ignore
          steps[i].position = 'left';
        }
      }
    }

    return steps;
  };

  useEffect(() => {
    if (data?.bot?.name) {
      analytics.page('Bots', data?.bot?.name);
      trackEvent('Viewed Bot', {
        botId: data?.bot?.name,
      });
    }
  }, [data?.bot?.name]);

  useEffect(() => {
    setTourSteps(getTourSteps());
  }, [isConfirmModalVisible]);

  useEffect(() => {
    if (!botListingLoading) {
      if (isConfirmModalVisible) {
        const hasTouredListingPurchaseConfirm = localStorage.getItem(
          LOCAL_STORAGE.HAS_TOURED_LISTING_PURCHASE_CONFIRM,
        );

        // if (!hasTouredListingPurchaseConfirm) {
        //   setTimeout(() => {
        //     setTourOpen(true);
        //     blockScroll();
        //   }, 500);
        // }
      } else {
        const hasTouredListingPurchase = localStorage.getItem(
          LOCAL_STORAGE.HAS_TOURED_LISTING_PURCHASE,
        );

        // if (!hasTouredListingPurchase) {
        //   setTourOpen(true);
        //   blockScroll();
        // }
      }
    }
  }, [botListingLoading, isConfirmModalVisible]);

  useEffect(() => {
    if (!botListingLoading) {
      setNumberOfListings({
        user: myOwnListingIds?.length || 0,
        all:
          (botListingData?.staffListingsCount || 0) +
          (botListingData?.userListingsCount || 0),
      });
    }
  }, [botListingLoading, botListingData, myOwnListingIds]);

  if (!data.bot) {
    return <NextError statusCode={404} />;
  }

  return (
    <>
      <Container>
        <NextSeo title={metaTitle} description={metaDescription} />
        <Head>
          <meta
            name="viewport"
            content="initial-scale=1.0, width=device-width"
          />
          <meta
            property="og:url"
            content={`${WHITE_LABEL_URL}/bots/${router.query.id}`}
          />
          <meta property="og:image" content={data.bot.imageUrl} />
          <meta name="twitter:site" content={WHITE_LABEL_TWITTER} />
          <meta name="twitter:title" content={metaTitle} />
          <meta name="twitter:description" content={metaDescription} />
          <meta name="twitter:image" content={data.bot.imageUrl} />
          <meta name="twitter:card" content="summary_large_image" />
          <link rel="canonical" href={`${TIDAL_URL}/bots/${router.query.id}`} />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Product',
                description: metaDescription,
                name: data?.bot?.name,
                image: data?.bot?.imageUrl,
                url: `${WHITE_LABEL_URL}/bots/${router.query.id}`,
                offers: {
                  '@type': 'Offer',
                  availability: 'https://schema.org/InStock',
                  price: data?.bot?.lastRental?.priceCents
                    ? Number(data?.bot?.lastRental?.priceCents / 100).toFixed(2)
                    : '5.00',
                  priceCurrency: 'USD',
                },
                review: [],
              }),
            }}
          ></script>
        </Head>

        {/* Body Container Desktop */}
        <div className="flex flex-col md:flex-row flex-1 px-2 md:px-4 md:mb-4 md:space-x-4 max-w-7xl w-full mx-auto md:overflow-hidden">
          {/* Info Side Container */}
          <div className="flex-col flex flex-1 pt-2 md:pt-4">
            {/* Info Header */}
            <div className="relative bg-white rounded-lg">
              <img
                alt=""
                className="m-auto rounded-lg"
                src={data.bot.imageUrl}
              />
              <img
                className="absolute left-4 -bottom-4 border-4 border-white h-16 w-16 rounded-2xl"
                src={data.bot.imageUrl}
                alt="Balko"
              />
            </div>
            <div className="flex justify-between items-center py-4 pt-8 border-b-2 border-gray-100">
              <div className="flex items-center">
                <div className="flex flex-col items-start justify-items-center ml-2">
                  <div className="flex flex-row">
                    <span className="text-xl font-bold text-gray-600">
                      {data.bot.name}
                    </span>
                    {data.bot.listingMode === 'DIRECT' && (
                      <>
                        <BadgeCheck
                          color={'#FFFFFF'}
                          fill={WHITE_LABEL_ACCENT_COLOR}
                          className="ml-2"
                          data-tooltip-content={`Partnered with ${WHITE_LABEL_BRAND}`}
                          data-tooltip-id={`${data.bot.name}-partner-tooltip`}
                        />
                        <Tooltip
                          id={`${data.bot.name}-partner-tooltip`}
                          place="right"
                        />
                      </>
                    )}
                  </div>

                  <span className="text-sm font-normal text-gray-400">
                    {dataLoading ? (
                      <div role="status" className="">
                        <svg
                          aria-hidden="true"
                          className="w-4 h-4 mr-2 mt-2 animate-spin text-gray-200"
                          style={{
                            fill: WHITE_LABEL_ACCENT_COLOR,
                          }}
                          viewBox="0 0 100 101"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor"
                          />
                          <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentFill"
                          />
                        </svg>
                        <span className="sr-only">Loading...</span>
                      </div>
                    ) : (
                      <>
                        {data.bot.listingMode === 'DIRECT'
                          ? 100
                          : numberOfListings.all + ' '}{' '}
                        Total Listing
                        {(data.bot.listingMode === 'DIRECT'
                          ? 100
                          : numberOfListings.all) > 1 && 's'}
                      </>
                    )}
                  </span>
                  {numberOfListings.user > 0 && (
                    <span className="text-xs font-normal text-gray-400 ml-2">
                      {`- You own ${
                        numberOfListings.all == numberOfListings.user
                          ? 'all'
                          : numberOfListings.user
                      } of those listing${numberOfListings.all > 1 ? 's' : ''}`}
                    </span>
                  )}
                </div>
              </div>
              {data.bot.listingMode === 'SELLER' && (
                <div className="w-1/3 lg:w-1/4">
                  <Button
                    onClick={onCreateListing}
                    backgroundColor={WHITE_LABEL_ACCENT_COLOR}
                    isLoading={getMeLoading}
                    isDisabled={getMeLoading}
                    color={'#fff'}
                    text={'List for Rent'}
                  />
                </div>
              )}
            </div>

            {/* Bot Info */}
            <div className="py-4 px-2 text-left border-b-2 border-gray-100">
              <div className="text-left">
                <div className="text-sm font-medium text-gray-400">
                  Description
                </div>
                <div className="text-sm font-normal text-gray-600 mt-1">
                  {description}
                </div>
              </div>
              <div className="pt-2 md:pt-4">
                <span className="text-sm font-medium text-gray-400 text items-center flex">
                  <GlobeIcon className="mr-1 h-5 w-5 text-gray-400" />
                  Supported Sites
                </span>

                <div className="flex gap-1 mt-2 flex-wrap">
                  {data.bot.tags.split(',').map((site) => (
                    <div className=" px-2 py-1 bg-gray-200 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-300">
                      {site}
                    </div>
                  ))}
                </div>
              </div>
              <div className="pt-2 md:pt-4">
                <span className="text-sm font-medium text-gray-400 text items-center flex">
                  <DesktopComputerIcon className="mr-1 h-5 w-5 text-gray-400" />{' '}
                  Systems
                </span>

                <div className="flex space-x-1 mt-2">
                  {data.bot.operatingSystem.split(',').map((system) => (
                    <div className=" px-2 py-1 bg-gray-200 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-300">
                      {system}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Links */}
            <div className="py-4 px-2 text-left border-b-2 border-gray-100 space-y-4 mb-2">
              <div className="text-left flex items-center justify-between">
                <div className="text-sm font-normal text-gray-600">Website</div>
                <a
                  href={data.bot.websiteUrl}
                  target="_website"
                  className="text-sm font-medium text-blue-400"
                  style={{
                    color: `${WHITE_LABEL_ACCENT_COLOR}`,
                  }}
                >
                  {data.bot.websiteUrl}
                </a>
              </div>
              <div className="text-left flex items-center justify-between">
                <div className="text-sm font-normal text-gray-600">Twitter</div>
                <a
                  href={`https://twitter.com/${data.bot.twitterHandle}`}
                  target="_twitter"
                  className="text-sm font-medium text-blue-400"
                  style={{
                    color: `${WHITE_LABEL_ACCENT_COLOR}`,
                  }}
                >
                  {data.bot.twitterHandle}
                </a>
              </div>
              {data.bot.instagramHandle && (
                <div className="text-left flex items-center justify-between">
                  <div className="text-sm font-normal text-gray-600">
                    Instagram
                  </div>
                  <a
                    href={`https://instagram.com/${data.bot.instagramHandle}`}
                    target="_instagram"
                    className="text-sm font-medium text-blue-400"
                    style={{
                      color: `${WHITE_LABEL_ACCENT_COLOR}`,
                    }}
                  >
                    {data.bot.instagramHandle}
                  </a>
                </div>
              )}
            </div>
            <div
              className="hidden md:flex md:flex-col md:overflow-y-auto"
              data-tour="step-2"
            >
              <span className="px-2 mt-3 mb-3 text-xl font-medium text-gray-600">
                Activity
              </span>
              <div className="px-2 mt-2 overflow-auto">
                <RentalHistory
                  botName={data.bot.name}
                  history={data.bot.orders}
                />
              </div>
            </div>
          </div>
          <RentalSideContainer
            bot={data.bot}
            username={props.username}
            affiliateId={props.affiliateId}
            setIsConfirmModalVisible={setIsConfirmModalVisible}
            isConfirmModalVisible={isConfirmModalVisible}
            blockScroll={blockScroll}
            setTourOpen={setTourOpen}
            myOwnListingIds={myOwnListingIds}
            refetchBot={refetchBot}
          />
        </div>
        {props.summary ? (
          <div className="px-2 md:px-4 md:mb-4 md:space-x-4 max-w-7xl w-full mx-auto md:overflow-hidden bot-summary-container">
            <Box height={60} />
            <div style={{ width: '100%', border: '1px solid #CACFE0' }}></div>
            <Box height={20} />
            <HeadingOne>{props.summary.title}</HeadingOne>
            <BotSummaryBody
              dangerouslySetInnerHTML={{ __html: props.summary.content }}
            />
          </div>
        ) : null}
        {/* <div
          id="bottom-banner"
          tabIndex={-1}
          className="fixed bottom-0 left-0 z-50 flex justify-between w-full p-4 border-t border-gray-200"
          style={{
            backgroundColor: WHITE_LABEL_ACCENT_COLOR,
          }}
        >
          <div className="flex items-center mx-auto">
            <p className="flex items-center text-sm font-normal text-white">
              <span className="inline-flex p-1 mr-3 bg-transparent rounded-full dark:bg-gray-600">
                <svg
                  className="w-4 h-4 bg-transparent"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    clip-rule="evenodd"
                    fill-rule="evenodd"
                    d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm2.5 3a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6.207.293a1 1 0 00-1.414 0l-6 6a1 1 0 101.414 1.414l6-6a1 1 0 000-1.414zM12.5 10a1.5 1.5 0 100 3 1.5 1.5 0 000-3z"
                  ></path>
                </svg>
                <span className="sr-only">Discount coupon icon</span>
              </span>
              <span>
                Get 5% commision per sale{' '}
                <a
                  href="https://flowbite.com"
                  className="flex items-center ml-0 text-sm font-medium text-blue-500 md:ml-1 md:inline-flex dark:text-blue-500 hover:underline"
                >
                  Become an affiliate{' '}
                  <svg
                    aria-hidden="true"
                    className="w-4 h-4 ml-1 text-blue-500 dark:text-blue-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillrule="evenodd"
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                      cliprule="evenodd"
                    ></path>
                  </svg>
                </a>
              </span>
            </p>
          </div>
          <div className="flex items-center">
            <button
              data-dismiss-target="#bottom-banner"
              type="button"
              className="flex-shrink-0 inline-flex justify-center items-center text-gray-400 hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 dark:hover:bg-gray-600 dark:hover:text-white"
            >
              <svg
                aria-hidden="true"
                className="w-4 h-4"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                ></path>
              </svg>
              <span className="sr-only">Close banner</span>
            </button>
          </div>
        </div> */}
      </Container>

      <Tour
        steps={tourSteps}
        isOpen={isTourOpen}
        onRequestClose={() => closeTour()}
        disableInteraction={true}
        showNumber={false}
        rounded={5}
        startAt={tourStep}
        showNavigation={false}
        showCloseButton={false}
        scrollDuration={25}
        lastStepNextButton={
          <button
            onClick={() => closeTour()}
            className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-500 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Got it !
          </button>
        }
      />
    </>
  );
}

export async function getStaticPaths() {
  const apolloClient = initializeApollo();

  const { data } = await apolloClient.query({
    query: ALL_BOTS_LIST,
  });

  return {
    paths: data?.bots?.map((bot) => {
      return `/bots/${bot.id}`;
    }),
    fallback: true,
  };
}

export async function getStaticProps(ctx) {
  const apolloClient = initializeApollo();
  let props = {};

  try {
    try {
      const sum: any = getBotSummaryBySlug(ctx.params.id, [
        'content',
        'title',
        'cover_image',
      ]);

      if (sum && !process.env.WHITE_LABEL_ORG) {
        props = {
          ...props,
          summary: {
            content: await markdownToHtml(sum.content || ''),
            title: sum.title,
            coverImage: sum.cover_image,
          },
        };
      }
    } catch (error) {
      console.log(error);
    }

    const { data } = await apolloClient.query({
      query: BOT,
      variables: {
        id: ctx.params.id,
      },
    });
    if (!data?.bot) {
      throw new Error('Recieved invalid data for bot');
    }
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
      revalidate: 60,
    };
  }

  return addApolloState(apolloClient, {
    props,
    revalidate: 3600,
  });
}

export default Expanded;
